import React from 'react';
import { Check, Cloud, Maximize, Minimize, GraduationCap, Sparkles, Sun, Moon, Settings } from 'lucide-react';
import { useLearn } from '../../contexts/LearnContext';

interface LayoutHeaderProps {
    pageTitle: string;
    isFullScreen: boolean;
    setIsFullScreen: (full: boolean) => void;
    startTour: () => void;
    isDarkMode: boolean;
    toggleTheme: () => void;
}

export const LayoutHeader: React.FC<LayoutHeaderProps> = ({
    pageTitle,
    isFullScreen,
    setIsFullScreen,
    startTour,
    isDarkMode,
    toggleTheme
}) => {
    const { setIsLearnOpen } = useLearn();

    return (
        <header className="flex-shrink-0 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-sm">
            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-4">
                    <h1 className="text-lg font-semibold text-[var(--text-primary)]">
                        {pageTitle}
                    </h1>
                    <div className="flex items-center gap-2 px-2 py-1 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <Check size={14} className="text-green-500" />
                        <span className="text-xs font-medium text-green-600">Saved</span>
                        <Cloud size={14} className="text-green-500" />
                        <span className="text-xs font-medium text-green-600">Synced</span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className={`
                            flex items-center gap-2 px-3 py-2 rounded-lg transition-all shadow-sm border-2 
                            ${isFullScreen
                                ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                : 'border-blue-500/50 bg-blue-500/10 text-blue-500 hover:bg-blue-50'
                            }
                        `}
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? (
                            <>
                                <Minimize size={18} />
                                <span className="font-bold">Exit Focus</span>
                            </>
                        ) : (
                            <>
                                <Maximize size={18} />
                                <span className="font-bold">Focus Mode</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => setIsLearnOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all shadow-sm border-2 border-emerald-500 bg-emerald-500 text-white"
                        title="Open Magnus Academy"
                    >
                        <GraduationCap size={18} />
                        <span className="font-bold">Academy</span>
                    </button>

                    <button
                        onClick={startTour}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all shadow-sm border border-[var(--border-color)] bg-[var(--bg-primary)]"
                    >
                        <Sparkles size={18} className="text-amber-500" />
                        <span className="text-[var(--text-secondary)] font-medium">Tour</span>
                    </button>

                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                    >
                        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    <button className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
                        <Settings size={20} />
                    </button>
                </div>
            </div>
        </header>
    );
};
